// dependencies
import React, { useMemo } from "react";

// types
import { IEmployeeWithoutGoalsDocument } from "@performyard/models";
// components
import {
	Avatar,
	ICellRendererParams,
	Link,
	PYColumnDef,
	SortChangedEvent,
	Table,
	Text,
} from "@performyard/py-components";

// hooks
import { SearchServiceSort } from "../../../../../../api/hooks";

// utils
import { employeeUrlWithFrom } from "../../../../../../utils/employeeDetailsBreadcrumbs";

interface EmployeesWithoutGoalsTableProps {
	employees: IEmployeeWithoutGoalsDocument[];
	isFetchingEmployees: boolean;
	handleColumnHeaderClick: (columnId: SearchServiceSort) => void;
	activeSort: string;
	activeSortDirection: string;
	groupsEnabled: boolean;
}

// Custom cell renderer for employee name with avatar
const EmployeeNameCellRenderer = (params: ICellRendererParams<IEmployeeWithoutGoalsDocument>) => {
	const employee = params.data;
	if (!employee) return null;

	const employeeID = employee._id;
	const employeeName = employee.name;

	return (
		<div className="flex h-full w-full max-w-full min-w-0 items-center justify-start gap-1">
			<Link href={employeeUrlWithFrom(employeeID)} forceHardReload underline="hover">
				<div className="flex items-center gap-1">
					<Avatar size={20} id={employeeID.toString()} name={employeeName} />
					<div className="line-clamp-1">
						<Text size="paragraph-3" color="primary-5" weight="regular">
							{employeeName}
						</Text>
					</div>
				</div>
			</Link>
		</div>
	);
};

// Custom cell renderer for manager with avatar
const ManagerCellRenderer = (params: ICellRendererParams<IEmployeeWithoutGoalsDocument>) => {
	const employee = params.data;
	if (!employee) return null;

	const managerID = employee.manager?._id;
	const managerName = employee.manager?.name;

	if (!managerID) return null;

	return (
		<div className="flex h-full w-full max-w-full min-w-0 items-center justify-start gap-1">
			<Link href={employeeUrlWithFrom(managerID)} forceHardReload underline="hover">
				<div className="flex items-center gap-1">
					<Avatar size={20} id={managerID.toString()} name={managerName} />
					<div className="line-clamp-1">
						<Text size="paragraph-3" color="primary-5" weight="regular">
							{managerName}
						</Text>
					</div>
				</div>
			</Link>
		</div>
	);
};

// Create column definitions
const createColumnDefs = (groupsEnabled: boolean): PYColumnDef<IEmployeeWithoutGoalsDocument>[] => {
	const columns: PYColumnDef<IEmployeeWithoutGoalsDocument>[] = [];

	const columnWidth = groupsEnabled ? 365 : 548;

	// Employee column
	columns.push({
		headerName: "Employee",
		field: "name",
		cellRenderer: EmployeeNameCellRenderer,
		width: columnWidth,
		sortable: true,
		resizable: false,
	});

	// Group column (conditional)
	if (groupsEnabled) {
		columns.push({
			headerName: "Group",
			field: "department.name",
			type: "text",
			width: columnWidth,
			sortable: true,
			resizable: false,
			valueGetter: (params) => params.data?.group ?? "",
		});
	}

	// Manager column
	columns.push({
		headerName: "Manager",
		field: "manager.name",
		cellRenderer: ManagerCellRenderer,
		width: groupsEnabled ? 366 : 548, // Extra pixel for 3 columns to reach 1096
		sortable: true,
		resizable: false,
	});

	return columns;
};

const EmployeesWithoutGoalsTable = (props: EmployeesWithoutGoalsTableProps) => {
	const { employees, isFetchingEmployees, handleColumnHeaderClick, activeSort, activeSortDirection, groupsEnabled } =
		props;

	// Memoize column definitions to prevent recreation on every render
	const columnDefs = useMemo(() => createColumnDefs(groupsEnabled), [groupsEnabled]);

	// Handle sort changes from AG Grid
	const onSortChanged = (event: SortChangedEvent<IEmployeeWithoutGoalsDocument>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn && sortedColumn.colId) {
			handleColumnHeaderClick({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	return (
		<div data-testid="employeesWithoutGoalsTableContainer">
			<Table<IEmployeeWithoutGoalsDocument>
				autoHeight
				columnSizing="fit-grid"
				rowData={employees}
				columnDefs={columnDefs}
				loading={isFetchingEmployees}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText={"No Employees Found"}
				// Set initial sort state from props
				initialState={{
					sort: {
						sortModel: activeSort
							? [
									{
										colId: activeSort,
										sort: activeSortDirection as "asc" | "desc",
									},
								]
							: [],
					},
				}}
				animateRows={false}
			/>
		</div>
	);
};

export default EmployeesWithoutGoalsTable;
